/**
 * Gemini extraction contract (PROMPT §8). Gemini READS the flash and returns
 * strict JSON (no markdown); we validate it with zod and retry on parse failure.
 * Gemini never does the closing/projection arithmetic (§18) — only extraction.
 */
import { z } from 'zod';
import { round2, safeDiv } from './money.js';
import { SEDE_CODES } from './types.js';
import type { RegistroDiario } from './types.js';

const num = z.number().finite().nullable().optional();
const int = z.number().int().nullable().optional();

const segment = z.object({ rev_bs: num, rooms: int }).partial().nullable().optional();

export const flashDaySchema = z.object({
  total_rooms: int,
  ooo: int,
  oos: int,
  ocupadas_sin_ch: int,
  house: int,
  comp: int,
  rooms_occupied: int, // "Rooms Occupied" DAY (for identity check §2.3.3)
  adr_bs: num,
  pct_ocupacion_sin_ch: num,
  total_revenue_bs: num,
  room_revenue_bs: num,
  fnb_revenue_bs: num,
  individual: segment,
  company: segment,
  travel_agent: segment,
  block: segment,
  persons: z.object({ total: int, adults: int, children: int }).partial().nullable().optional(),
  avg_person_rate_bs: num,
  revpar_bs: num,
  walkin: int,
  day_use: int,
  no_show: int,
  early_departure: int,
  extended_stay: int,
  reservations_made: int,
  cancellations: int,
  pace: z.object({ next7: num, next31: num, next365: num }).partial().nullable().optional(),
});

export const flashMonthSchema = z.object({
  rooms_occupied: int,
  ocupadas_sin_ch: int,
  house: int,
  comp: int,
  pct_ocupacion_sin_ch: num,
  room_revenue_bs: num,
  total_revenue_bs: num,
});

export const geminiFlashSchema = z.object({
  sede_codigo: z.enum(SEDE_CODES).nullable(),
  fecha_operacion: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'fecha_operacion debe ser YYYY-MM-DD')
    .nullable(),
  day: flashDaySchema,
  month: flashMonthSchema.partial().nullable().optional(),
});

export type GeminiFlash = z.infer<typeof geminiFlashSchema>;

/**
 * Normalize a raw Gemini object to the `{ sede_codigo, fecha_operacion, day,
 * month }` shape BEFORE zod parsing. The model sometimes drops the "day" wrapper
 * and flattens the day-level fields to the top level, which zod rejects with a
 * hard "day Required" error (a full extraction failure). When we see day-like
 * keys at the top and no "day" object, we rewrap them. Idempotent and safe when
 * the shape is already correct. The key list is derived from the schema, so it
 * never drifts. (`month` may also arrive flattened, but it is optional so a miss
 * only costs the MTD control column, not the whole extraction.)
 */
export function normalizeFlashShape(obj: unknown): unknown {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const o = obj as Record<string, unknown>;
  if (o.day && typeof o.day === 'object') return o; // already correct shape
  const dayKeys = Object.keys(flashDaySchema.shape);
  const day: Record<string, unknown> = {};
  let found = 0;
  for (const k of dayKeys) {
    if (k in o) {
      day[k] = o[k];
      found++;
    }
  }
  if (found === 0) return o; // nothing day-like to rewrap; let zod report it
  return {
    sede_codigo: o.sede_codigo ?? null,
    fecha_operacion: o.fecha_operacion ?? null,
    day,
    month: o.month ?? null,
  };
}

/**
 * The system/extraction prompt for Gemini. Identifies sede + operation date,
 * extracts only the 2026 DAY / 2026 MONTH columns, and NEVER invents Diamond.
 */
export const GEMINI_EXTRACTION_PROMPT = `Eres un extractor de datos de reportes hoteleros "Manager - Flash".
Lee el PDF/imagen y devuelve EXCLUSIVAMENTE un objeto JSON válido (sin markdown, sin texto adicional) con este contrato.

Reglas:
1. Identifica la SEDE por el encabezado del reporte. Devuelve uno de: ${SEDE_CODES.join(', ')} en "sede_codigo" (o null si no es claro).
2. Identifica la FECHA DE OPERACIÓN desde el filtro "Calendar/Month to Date" (la jornada reportada), NO la fecha de generación del PDF (que es el día siguiente). Devuélvela en formato YYYY-MM-DD.
   - Las fechas en el flash están en formato VENEZOLANO día/mes/año (DD/MM/AAAA). Al convertir, NO inviertas día y mes: p. ej. "01/06/2026" es 2026-06-01 (1 de junio), NO 2026-01-06.
   - Coherencia: la fecha de operación = fecha de generación del PDF − 1 día. Úsala para desambiguar día vs mes si el formato no es claro.
3. El reporte tiene 6 columnas numéricas, SIEMPRE en este orden de izquierda a derecha: (1) DAY del año en curso, (2) MONTH del año en curso, (3) YEAR del año en curso, (4) DAY del año anterior, (5) MONTH del año anterior, (6) YEAR del año anterior (p. ej. 2026 DAY / 2026 MONTH / 2026 YEAR / 2025 DAY / 2025 MONTH / 2025 YEAR).
   - Para el objeto "day" toma SIEMPRE la PRIMERA columna numérica (la inmediatamente a la derecha de la etiqueta de cada fila) = "<año en curso> DAY". NUNCA uses MONTH, YEAR ni ninguna columna del año anterior para "day".
   - Para el objeto "month" toma la SEGUNDA columna numérica = "<año en curso> MONTH" (acumulado month-to-date, para control de cuadre).
   - Alinea fila por fila: localiza la etiqueta a la izquierda y desplázate por SU MISMA fila hasta la columna correcta. No mezcles valores de filas vecinas.
4. NO inventes "Diamond": ese dato NO aparece en el flash y se carga aparte. No lo incluyas.
5. Si un campo no está presente, usa null. No estimes ni calcules; solo transcribe lo que ves.
6. Números en formato venezolano ("1.234,56") conviértelos a número decimal estándar (1234.56).
7. VERIFICACIÓN OBLIGATORIA antes de responder (coherencia interna de la columna DAY): tus valores deben cumplir estas identidades:
   - "ADR minus Comp and House" ≈ "Room Revenue" ÷ "Rooms Occupied minus Comp and House".
   - "Revenue per Available Room" (RevPAR) ≈ "Room Revenue" ÷ "Total Rooms in Hotel".
   Si NO se cumplen (diferencia mayor a ~2%), estás leyendo la columna o la fila equivocada: vuelve a alinear en la PRIMERA columna numérica y corrige los números ANTES de emitir el JSON. Esto es lo más importante: la mayoría de los errores vienen de tomar la columna MONTH o del año anterior por error.

Mapeo de etiquetas del flash → campos:
- "Total Rooms in Hotel" → total_rooms
- "Out of Order Rooms" → ooo ; "Out of Service Rooms" → oos
- "Rooms Occupied minus Comp and House Use" → ocupadas_sin_ch
- "Rooms Occupied" → rooms_occupied
- "House Use Rooms" → house ; "Complimentary Rooms" → comp
- "ADR minus Comp and House" → adr_bs
- "% Rooms Occupied minus Comp and House" → pct_ocupacion_sin_ch (como fracción 0..1)
- "Total Revenue" → total_revenue_bs ; "Room Revenue" → room_revenue_bs
- "Food And Beverage Revenue" → fnb_revenue_bs
- "Individual/Company/Travel Agent/Block Revenue" → individual/company/travel_agent/block.rev_bs y .rooms
- "Total In-House Persons/Adults/Children" → persons.total/adults/children
- "Average Person Rate" → avg_person_rate_bs ; "Revenue per Available Room" → revpar_bs
- "Walk-in/Day Use/No Show/Early Departure/Extended Stay Rooms" → walkin/day_use/no_show/early_departure/extended_stay
- "Reservations Made Today" → reservations_made ; "Cancelled Reservations" → cancellations
- "% Rooms Occupied for the Next 7/31/365 Days" → pace.next7/next31/next365 (fracción 0..1)`;

/**
 * Map a validated Gemini extraction onto a (partial) daily record.
 *
 * ADR and RevPAR are NOT trusted from the model: the engine RECOMPUTES them
 * deterministically from Room Revenue and the room counts (§8/§18 — the engine
 * owns the arithmetic, the model only reads). This makes them immune to the
 * model grabbing the wrong column or misreading the ADR/RevPAR cell (a frequent
 * failure on the dense 6-column flash table, and the cause of the intermittent
 * wrong ADR/RevPAR). We fall back to the transcribed value only when an input
 * needed to compute is missing:
 *   - ADR ("minus Comp & House") = Room Revenue ÷ Rooms Occupied (sin Comp/House)
 *   - RevPAR = Room Revenue ÷ Total Rooms (physical inventory, incl. OOO — the
 *     same base the flash uses, see rules.ts DerivedOptions).
 * Everything else is a pure field mapping. The model's raw ADR/RevPAR read is
 * kept for a divergence check in validateRegistro (adrBsRead/revparBsRead).
 */
export function geminiToRegistro(
  g: GeminiFlash,
  sedeId: number,
): Omit<RegistroDiario, 'tasaBcv' | 'tarifaUsd' | 'pctOcupacion' | 'fecha'> & {
  fecha: string | null;
} {
  const d = g.day;
  const adrComputed = safeDiv(d.room_revenue_bs, d.ocupadas_sin_ch);
  const revparComputed = safeDiv(d.room_revenue_bs, d.total_rooms);
  return {
    sedeId,
    fecha: g.fecha_operacion,
    totalRooms: d.total_rooms ?? null,
    ooo: d.ooo ?? null,
    oos: d.oos ?? null,
    ocupadasSinCh: d.ocupadas_sin_ch ?? null,
    house: d.house ?? null,
    comp: d.comp ?? null,
    diamond: null, // NEVER from Gemini (§2.2)
    // Recomputed, not transcribed (see JSDoc). Fallback to the read if we lack
    // Room Revenue / room counts to derive it.
    adrBs: adrComputed != null ? round2(adrComputed) : (d.adr_bs ?? null),
    totalRevenueBs: d.total_revenue_bs ?? null,
    roomRevenueBs: d.room_revenue_bs ?? null,
    fnbRevenueBs: d.fnb_revenue_bs ?? null,
    individualRevBs: d.individual?.rev_bs ?? null,
    individualRooms: d.individual?.rooms ?? null,
    companyRevBs: d.company?.rev_bs ?? null,
    companyRooms: d.company?.rooms ?? null,
    taRevBs: d.travel_agent?.rev_bs ?? null,
    taRooms: d.travel_agent?.rooms ?? null,
    blockRevBs: d.block?.rev_bs ?? null,
    blockRooms: d.block?.rooms ?? null,
    inHousePersons: d.persons?.total ?? null,
    inHouseAdults: d.persons?.adults ?? null,
    inHouseChildren: d.persons?.children ?? null,
    avgPersonRateBs: d.avg_person_rate_bs ?? null,
    revparBs: revparComputed != null ? round2(revparComputed) : (d.revpar_bs ?? null),
    walkin: d.walkin ?? null,
    dayUse: d.day_use ?? null,
    noShow: d.no_show ?? null,
    earlyDeparture: d.early_departure ?? null,
    extendedStay: d.extended_stay ?? null,
    reservationsMade: d.reservations_made ?? null,
    cancellations: d.cancellations ?? null,
    paceNext7: d.pace?.next7 ?? null,
    paceNext31: d.pace?.next31 ?? null,
    paceNext365: d.pace?.next365 ?? null,
    origen: 'gemini',
  };
}
